import React, { useEffect, useRef } from "react";
import { FormItem, SurveyFormStyle, TextAnswerFormat, TextChoiceAnswerFormat } from "../../../../types/Survey";
import Question from "../SurveyQuestion/SurveyQuestion";
import { isFieldsetFormat } from "../../../../helpers/answerFormatHelper";
import "./SurveyForm.css"
import { QuestionResultModel, ResultModel } from "../../../../types/SurveyResult";
import { isMultilineFormItem, sectionCanBeInHorizontalLayout } from "../../Util/formLayout";

export interface SurveyFormProps {
    formItems: FormItem[];
    results: ResultModel[];
    onResultChange: (result: ResultModel) => void;
    onEnterPressed: () => void;
    variant?: SurveyFormStyle;
    suppressDesignerFormItemSelection?: boolean;
}

export default function SurveyForm(props: SurveyFormProps) {
    const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
    let sections: FormItem[][] = [];
    let currentSection: FormItem[] | null = null;
    for (var i = 0; i < props.formItems.length; i++) {
        if (!currentSection || isMultilineFormItem(props.formItems[i], props.variant) || isMultilineFormItem(props.formItems[i - 1], props.variant)) {
            currentSection = [props.formItems[i]];
            sections.push(currentSection);
        }
        else {
            currentSection.push(props.formItems[i])
        }
    }

    let classes = ["survey-form"];
    if (props.variant == "cardBased") {
        classes.push("survey-form-card-based");
    }
    if (props.variant == "v2") {
        classes.push("survey-form-v2");
    }

    useEffect(() => {
        itemsRef.current = itemsRef.current.slice(0, props.formItems.length);
    }, [props.formItems]);

    let singleChoiceResultSelected = (formSectionIndex: number, result: ResultModel) => {
        props.onResultChange(result);

        let questionResult = result as QuestionResultModel;
        const nextFormSection = getNextFormSection(formSectionIndex);
        if (nextFormSection) {
            if (nextFormSection && ((questionResult.Choices?.length || 0) !== 0 || questionResult.Answer)) {
                commitFormAnswer(formSectionIndex);
                nextFormSection.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }
    }

    function isSingleChoiceTextChoice(formItem: FormItem) {
        return formItem.answerFormat?.type == "TextChoiceAnswerFormat" && (formItem.answerFormat as TextChoiceAnswerFormat).style == "Single"
            || formItem.answerFormat?.type == "BooleanAnswerFormat";
    }

    function getNextFormSection(formSectionIndex: number): HTMLElement | null {
        if (formSectionIndex < itemsRef.current.length - 1) {
            return itemsRef.current[formSectionIndex + 1];
        }

        return null;
    }

    function commitFormAnswer(formSectionIndex: number) {
        const nextFormSection = getNextFormSection(formSectionIndex);
        if (!nextFormSection) {
            // This was the last form item
            props.onEnterPressed();
        }
    }

    function normalizeIdentifier(identifier: string | undefined): string {
        return (identifier || "").toString().replace(/[^a-zA-Z0-9_-]/g, "_");
    }

    return <div className={classes.join(" ")}>
        {sections.map((s, i) =>
            <div className="survey-form-section" key={i} ref={el => itemsRef.current[i] = el} style={{ minWidth: (props.variant == "v2" && sectionCanBeInHorizontalLayout(s)) ? `${s[0].minimumWidth}px` : "100%" }}>
                {s.map((item, j) =>
                    <Question
                        key={item.identifier}
                        answerFormat={item.answerFormat}
                        label={item.text}
                        index={!props.suppressDesignerFormItemSelection ? props.formItems.indexOf(item) : undefined}
                        result={props.results.find(r => r.Identifier == item.identifier)!}
                        onResultChange={(r) => isSingleChoiceTextChoice(item) ? singleChoiceResultSelected(i, r) : props.onResultChange(r)}
                        onEnterPressed={() => { commitFormAnswer(i); }}
                        optional={item.optional}
                        placeholder={item.placeholder}
                        labelPosition={isMultilineFormItem(item, props.variant) ? "top" : "left"}
                        inputIdForLabel={normalizeIdentifier(item.identifier)}
                        useFieldsetFormat={isFieldsetFormat(item.answerFormat)}
                        variant={props.variant} />
                )}
            </div>
        )}
    </div>
}